"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/app/v2/_components/button/button";
import { Column } from "@/app/v2/_components/column/Column";
import { Row } from "@/app/v2/_components/row/Row";
import { Text } from "@/app/v2/_components/text/Text";

export type ContactEnrichmentPreviewExample = {
  id: string;
  name: string;
  email: string;
  beforeFields: string[];
  afterFields: string[];
};

type ContactEnrichmentPreviewView = "before" | "after";

export const contactEnrichmentDemoExamples: ContactEnrichmentPreviewExample[] = [
  {
    id: "desi-garcia",
    name: "Desiree Garcia",
    email: "desiree@example.com",
    beforeFields: ["Old lead", "(813) 606-0184", "Tampa, FL"],
    afterFields: [
      "Address verified",
      "2 verified phones",
      "2 verified emails",
      "Age 38",
      "Female",
      "Tradesman household",
      "Owner profile enriched",
    ],
  },
  {
    id: "marcus-wade",
    name: "Marcus Wade",
    email: "marcus@example.com",
    beforeFields: ["Sphere lead", "(727) 555-0128", "St. Petersburg, FL"],
    afterFields: [
      "Value $615K",
      "Equity $287K",
      "47% equity",
      "Owner occupied",
      "3 bed",
      "2 bath",
      "Built 2004",
      "Owned 11 yrs",
    ],
  },
  {
    id: "sofia-patel",
    name: "Sofia Patel",
    email: "sofia@example.com",
    beforeFields: ["Past client", "(727) 555-0199", "Clearwater, FL"],
    afterFields: [
      "Address verified",
      "Absentee owner",
      "Free & clear",
      "High equity",
      "Likely investor",
      "2 properties",
      "Audience ready",
    ],
  },
];

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[,%$,\s]/g, "");
    if (!normalized) return undefined;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    if (normalized === "TRUE") return true;
    if (normalized === "FALSE") return false;
  }
  return undefined;
}

function coalesceString(...values: unknown[]) {
  for (const value of values) {
    const next = asString(value);
    if (next) return next;
  }
  return undefined;
}

function firstRecord(values: unknown[]) {
  for (const value of values) {
    const next = asRecord(value);
    if (next) return next;
  }
  return undefined;
}

function formatCurrencyShort(value: number) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function formatPhone(value?: string) {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value;
}

function formatLocation(city?: string, state?: string) {
  if (city && state) return `${city}, ${state}`;
  return city || state;
}

function countTruthyStrings(values: Array<unknown>) {
  return values.reduce((count, value) => count + (asString(value) ? 1 : 0), 0);
}

function dedupe(values: Array<string | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

export function buildContactEnrichmentPreviewExample(
  contact: Record<string, unknown>,
): ContactEnrichmentPreviewExample {
  const flattened = asRecord(contact.flattened);
  const property = asRecord(contact.property);
  const propertyInfo = asRecord(property?.propertyInfo);
  const ownerInfo = asRecord(property?.ownerInfo);
  const lastSale = asRecord(property?.lastSale);
  const skipTrace = asRecord(contact.skipTrace);
  const demographics = asRecord(skipTrace?.demographics);
  const primaryAddress =
    asRecord(contact.address) ||
    asRecord(propertyInfo?.address) ||
    firstRecord(asArray(skipTrace?.addresses)) ||
    undefined;

  const name =
    coalesceString(
      contact.name,
      flattened?.full_name,
      flattened?.skip_full_name,
      [asString(contact.firstName), asString(contact.lastName)].filter(Boolean).join(" "),
      [asString(flattened?.first_name), asString(flattened?.last_name)].filter(Boolean).join(" "),
    ) || "Unnamed contact";

  const email =
    coalesceString(contact.email, flattened?.primary_email, flattened?.email_1, flattened?.email_2) ||
    "contact@example.com";

  const phone = formatPhone(
    coalesceString(contact.phone, flattened?.primary_phone, flattened?.phone_1, flattened?.phone_2),
  );
  const location = formatLocation(
    coalesceString(primaryAddress?.city, flattened?.property_city, flattened?.mail_city, flattened?.skip_current_city),
    coalesceString(
      primaryAddress?.state,
      flattened?.property_state,
      flattened?.mail_state,
      flattened?.skip_current_state,
    ),
  );
  const stage = coalesceString(contact.stage, flattened?.stage);
  const firstTag = asArray(contact.tags).find((tag) => asString(tag));

  const beforeFields = dedupe([stage, phone, location, asString(firstTag)]).slice(0, 3);

  const estimatedValue = asNumber(property?.estimatedValue ?? flattened?.estimated_value);
  const estimatedEquity = asNumber(property?.estimatedEquity ?? flattened?.estimated_equity);
  const equityPercent = asNumber(property?.equityPercent ?? flattened?.equity_percent);
  const bedrooms = asNumber(propertyInfo?.bedrooms ?? property?.bedrooms ?? flattened?.bedrooms);
  const bathrooms = asNumber(propertyInfo?.bathrooms ?? property?.bathrooms ?? flattened?.bathrooms);
  const livingSqft = asNumber(
    propertyInfo?.livingSquareFeet ??
    propertyInfo?.squareFootage ??
    property?.livingSquareFeet ??
    flattened?.living_sqft,
  );
  const yearBuilt = asNumber(propertyInfo?.yearBuilt ?? property?.yearBuilt ?? flattened?.year_built);
  const ownerOccupied = asBoolean(ownerInfo?.ownerOccupied ?? property?.ownerOccupied ?? flattened?.owner_occupied);
  const absenteeOwner = asBoolean(property?.absenteeOwner ?? flattened?.absentee_owner);
  const freeClear = asBoolean(property?.freeClear ?? flattened?.free_clear);
  const highEquity = asBoolean(property?.highEquity ?? flattened?.high_equity);
  const ownershipLengthMonths = asNumber(ownerInfo?.ownershipLength ?? flattened?.ownership_length_months);
  const lastSalePrice = asNumber(
    lastSale?.saleAmount ?? lastSale?.price ?? propertyInfo?.lastSalePrice ?? flattened?.last_sale_price,
  );
  const lastSaleDate = coalesceString(lastSale?.saleDate, propertyInfo?.lastSaleDate, flattened?.last_sale_date);
  const saleYear = lastSaleDate ? new Date(lastSaleDate).getFullYear() : undefined;
  const ageRange = coalesceString(demographics?.age_range, demographics?.age, flattened?.age_range);
  const genderValue = coalesceString(demographics?.gender, flattened?.gender);
  const gender = genderValue === "F" ? "Female" : genderValue === "M" ? "Male" : genderValue;
  const occupation = coalesceString(
    demographics?.occupation,
    demographics?.occupationDescription,
    flattened?.occupation,
  );
  const addressVerified =
    asBoolean(skipTrace?.found) ||
    asBoolean(flattened?.property_looked_up) ||
    coalesceString(flattened?.address_source) === "skiptrace";
  const emailCount = Math.max(
    asArray(skipTrace?.emails).length,
    countTruthyStrings([flattened?.email_1, flattened?.email_2, flattened?.email_3]),
  );
  const phoneCount = Math.max(
    asArray(skipTrace?.phones).length,
    countTruthyStrings([flattened?.phone_1, flattened?.phone_2]),
  );

  const afterFields = dedupe([
    addressVerified ? "Address verified" : undefined,
    emailCount > 1 ? `${emailCount} verified emails` : undefined,
    phoneCount > 1 ? `${phoneCount} verified phones` : undefined,
    estimatedValue ? `Value ${formatCurrencyShort(estimatedValue)}` : undefined,
    estimatedEquity ? `Equity ${formatCurrencyShort(estimatedEquity)}` : undefined,
    equityPercent != null && equityPercent > 0 ? `${Math.round(equityPercent)}% equity` : undefined,
    bedrooms ? `${bedrooms} bed` : undefined,
    bathrooms ? `${bathrooms} bath` : undefined,
    livingSqft ? `${Math.round(livingSqft).toLocaleString()} sqft` : undefined,
    yearBuilt ? `Built ${yearBuilt}` : undefined,
    ownerOccupied === true ? "Owner occupied" : ownerOccupied === false ? "Non-owner occupied" : undefined,
    absenteeOwner ? "Absentee owner" : undefined,
    freeClear ? "Free & clear" : undefined,
    highEquity ? "High equity" : undefined,
    ownershipLengthMonths ? `Owned ${Math.max(1, Math.round(ownershipLengthMonths / 12))} yrs` : undefined,
    lastSalePrice && saleYear ? `Bought ${formatCurrencyShort(lastSalePrice)} in ${saleYear}` : undefined,
    ageRange ? `Age ${ageRange}` : undefined,
    gender,
    occupation,
  ]).slice(0, 8);

  return {
    id:
      coalesceString(
        contact._id,
        contact.rolloutContactId,
        flattened?.contact_id,
        flattened?.rollout_contact_id,
        email,
        name,
      ) || "contact-preview",
    name,
    email,
    beforeFields: beforeFields.length > 0 ? beforeFields : ["CRM record only"],
    afterFields: afterFields.length > 0 ? afterFields : ["Search-ready enrichment data"],
  };
}

function PreviewPill({ children, accent = false }: { children: React.ReactNode; accent?: boolean }) {
  if (accent) {
    return (
      <motion.div
        key={`${children}-accent-pill`}
        className="rounded-full border border-primary/15 px-3 py-1.5 bg-white/70 backdrop-blur-lg"
        transition={{ duration: 2, ease: [0.2, 0, 0, 1], delay: 3 }}
      >
        <Text sm className={cn("text-pretty text-primary")}>
          {children}
        </Text>
      </motion.div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-full border px-3 py-1.5 bg-white/70 backdrop-blur-lg",
        "border-black/5 bg-white text-foreground",
      )}
    >
      <Text sm className={cn("text-pretty")}>
        {children}
      </Text>
    </div>
  );
}

function BeforeAfterToggle({
  view,
  onChange,
}: {
  view: ContactEnrichmentPreviewView;
  onChange: (view: ContactEnrichmentPreviewView) => void;
}) {

  const beforeDomRef = useRef<HTMLButtonElement>(null);
  const afterDomRef = useRef<HTMLButtonElement>(null);

  const beforeWidth = useMemo(() => beforeDomRef.current?.clientWidth || 100, [beforeDomRef.current]);
  const afterWidth = useMemo(() => afterDomRef.current?.clientWidth || 100, [afterDomRef.current]);

  return (
    <div className="inline-flex rounded-full border border-black/5 bg-linear-to-b from-muted/20 to-muted/50 p-1 relative">
      <motion.div className={cn("absolute flex inset-0 p-1")} key={`toggle-background`}
      >
        <motion.div className="flex w-1/2 h-9 rounded-full bg-white border-[1px] border-border/50"
          initial={{ width: beforeWidth }}
          animate={{ width: view === "before" ? beforeWidth : afterWidth, x: view === "before" ? 0 : beforeWidth }}
          transition={{ type: "spring", visualDuration: 0.2, bounce: 0.2 }} />
      </motion.div>
      <button
        type="button"

        onClick={() => onChange("before")}
        className={cn(
          "relative rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-transform",
          view === "before" && "text-foreground",
        )}
        ref={beforeDomRef}
      >
        <span className="relative z-10">
          <Text sm>Before</Text>
        </span>
      </button>
      <button
        type="button"
        onClick={() => onChange("after")}
        className={cn(
          "relative rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-transform",
          view === "after" && "text-foreground",
        )}
        ref={afterDomRef}
      >
        <span className="relative z-10">
          <Text sm>After</Text>
        </span>
      </button>
    </div >
  );
}

function ContactEnrichmentExampleCard({
  example,
  view,
  showGrainientSweep,
}: {
  example: ContactEnrichmentPreviewExample;
  view: ContactEnrichmentPreviewView;
  showGrainientSweep: boolean;
}) {
  const addedFields = example.afterFields.length;
  const pillTransition = { duration: 0.2, ease: [0.2, 0, 0, 1] as const };
  const initials = example.name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <motion.div key={example.id} className={cn("relative w-full rounded-md bg-linear-to-b from-transparent to-muted/50", view === "after" && "from-transparent to-teal-50/50")}>
      <AnimatePresence initial={true}>
        <motion.div
          key={`${example.id}-grainient-sweep`}
          className="flex pointer-events-none absolute inset-0 z-10 overflow-hidden"
        >
          {showGrainientSweep ? (
            <motion.div
              key={`${example.id}-grainient-sweep`}
              className="flex pointer-events-none absolute h-auto w-full bg-linear-to-b from-transparent to-emerald-50 top-0 z-10"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: [1, 1, 1, 0], height: "150%" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 1.5, ease: [0, 0, 0.2, 1], delay: 0 }}
            ></motion.div>
          ) : null}
        </motion.div>
      </AnimatePresence>
      <motion.div
        key={example.id}
        className="relative w-full rounded-lg border border-border bg-transparent p-4 shadow-xs"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
      >
        <Column className="relative z-20 w-full bg-transparent" wide>
          <Text xs className="text-primary/80 uppercase tracking-widest">
            Example
          </Text>
          <Row align start>
            <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary shadow-xs">
              <Text sm fat className="text-primary">
                {initials}
              </Text>
            </div>

            <Column noGap>
              <Text fat className="text-foreground">
                {example.name}
              </Text>
              <Text sm muted>
                {example.email}
              </Text>
            </Column>
          </Row>

          <div className="w-full border-t border-black/5 pt-1">
            <Column className="gap-3 py-4" start>
              <Row align between className="gap-3">
                <Text sm fat className="text-foreground">
                  CRM record
                </Text>
                <Text sm subdued className="tabular-nums">
                  {`${example.beforeFields.length} fields`}
                </Text>
              </Row>

              <div className="flex flex-wrap gap-2">
                {example.beforeFields.map((field) => (
                  <motion.div key={`${example.id}-${field}`} transition={pillTransition}>
                    <PreviewPill>{field}</PreviewPill>
                  </motion.div>
                ))}
              </div>
            </Column>

            <AnimatePresence initial={false}>
              {view === "after" ? (
                <motion.div
                  key={`${example.id}-enriched-section`}
                  className="overflow-hidden border-t border-black/5 pt-1"
                  initial={{ height: 0, opacity: 0, y: -6 }}
                  animate={{ height: "auto", opacity: 1, y: 0 }}
                  exit={{ height: 0, opacity: 0, y: -4, transition: { delay: 0 } }}
                  transition={{ duration: 1, ease: [0, 0, 0, 1], delay: 0 }}
                >
                  <Column className="gap-3 pt-4 border-none" start>
                    <Row align between className="gap-3">
                      <Text sm fat className="text-foreground">
                        Enrichment added
                      </Text>
                      <Text sm className="tabular-nums text-primary">
                        {`+${addedFields} new data points`}
                      </Text>
                    </Row>

                    <div className="flex flex-wrap gap-2">
                      <AnimatePresence initial={false}>
                        {example.afterFields.map((field, index) => (
                          <motion.div
                            key={`${example.id}-after-${field}`}
                            initial={{ opacity: 0, y: 8, scale: 0.96, filter: "blur(4px)" }}
                            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                            exit={{ opacity: 0, y: -4, scale: 0.96, filter: "blur(4px)" }}
                            transition={{ type: "spring", duration: 0.3, bounce: 0, delay: index * 0.04 }}
                          >
                            <PreviewPill accent>{field}</PreviewPill>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </Column>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </Column>
      </motion.div>
    </motion.div>
  );
}

export function ContactEnrichmentPreview({
  title = "See what a contact looks like after enrichment",
  description = "Start with the CRM details you already have, then reveal the homeowner, property, and verification data Databank adds.",
  examples = contactEnrichmentDemoExamples,
  moreExamplesCount,
  defaultView = "before",
  className,
}: {
  title?: string;
  description?: string;
  examples?: ContactEnrichmentPreviewExample[];
  moreExamplesCount?: number;
  defaultView?: ContactEnrichmentPreviewView;
  className?: string;
}) {
  const items = examples.length > 0 ? examples : contactEnrichmentDemoExamples;
  const [view, setView] = useState<ContactEnrichmentPreviewView>(defaultView);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showGrainientSweep, setShowGrainientSweep] = useState(false);
  const grainientSweepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const current = items[currentIndex];
  const hasNext = items.length > 1;
  const remaining = moreExamplesCount ?? 0;

  useEffect(() => {
    return () => {
      if (grainientSweepTimeoutRef.current) {
        clearTimeout(grainientSweepTimeoutRef.current);
      }
    };
  }, []);

  const handleViewChange = (nextView: ContactEnrichmentPreviewView) => {
    if (nextView === view) return;

    if (grainientSweepTimeoutRef.current) {
      clearTimeout(grainientSweepTimeoutRef.current);
      grainientSweepTimeoutRef.current = null;
    }

    const shouldSweep = view === "before" && nextView === "after";
    setView(nextView);

    if (shouldSweep) {
      setShowGrainientSweep(true);
      grainientSweepTimeoutRef.current = setTimeout(() => {
        setShowGrainientSweep(false);
        grainientSweepTimeoutRef.current = null;
      }, 5000);
      return;
    }

    setShowGrainientSweep(false);
  };

  return (
    <Column className={cn("w-full gap-4", className)} wide start>
      <Row className="w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" start>
        <Column className="gap-1" wide start>
          <Row align tightGap shrink>
            <Text fat>{title}</Text>
            <ArrowRight className="size-4" />
          </Row>
          <Text sm subdued className="max-w-[64ch] text-pretty">
            {description}
          </Text>
        </Column>
        <BeforeAfterToggle view={view} onChange={handleViewChange} />
      </Row>

      <div className="w-full  relative">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={current.id} className="w-full">
            <ContactEnrichmentExampleCard example={current} view={view} showGrainientSweep={showGrainientSweep} />
          </motion.div>
        </AnimatePresence>

        <Row between align className="mt-4 gap-3">
          <Row align tightGap shrink className="w-fit">
            {items.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Show example ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
                className={cn(
                  "h-2 rounded-full transition-transform",
                  index === currentIndex ? "w-6 bg-primary" : "w-2 bg-black/10 hover:bg-black/20",
                )}
              />
            ))}
            {remaining > 0 ? (
              <Text sm muted className="ml-2 tabular-nums">
                +{remaining.toLocaleString()} more
              </Text>
            ) : null}
          </Row>

          {hasNext ? (
            <Button variant="ghost" size="sm" onClick={() => setCurrentIndex((currentIndex + 1) % items.length)}>
              <Text sm>
                Next example
              </Text>
              <ArrowRight className="size-4" />
            </Button>
          ) : null}
        </Row>
      </div>
    </Column>
  );
}
